import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { ServicesSocketService } from '../websocket/services-socket.service';
import { LocationSocketService } from '../websocket/location-socket.service';
import { VehiclesManagerService } from '../vehicles/vehicles-manager.service';
import {
    LocationEventData,
    MessageSource,
    ShiftEventData,
    ShiftEventType,
    WebSocketEvent
} from '../websocket/model/message.event';
import { ShiftState } from '../../shared/models/shift.model';
import { ToastService } from '../../shared/services/toast.service';
import { DriversManagerService } from '../drivers/drivers-manager.service';

@Injectable({
    providedIn: 'root'
})
export class ServerEventService {

    private isInitialized = false;
    private readonly openSubscriptions = Array<Subscription>();

    // handle derived layers source updated
    readonly derivedLayerCacheUpdateObservable = new Subject<Date>();

    constructor(private readonly serviceSocketService: ServicesSocketService,
                private readonly locationSocketService: LocationSocketService,
                private readonly vehiclesManager: VehiclesManagerService,
                private readonly driverManager: DriversManagerService,
                private readonly toast: ToastService,
    ) { }

    public init() {
        if (this.isInitialized) {
            throw Error('The ServerEventService has already been initialized.');
        }
        this.connectToManager();

        this.isInitialized = true;
    }

    public release() {
        if (!this.isInitialized) {
            return;
        }

        for (const subscription of this.openSubscriptions) {
            subscription.unsubscribe();
        }
        this.openSubscriptions.length = 0;
        this.isInitialized = false;
    }

    private connectToManager() {
        const servicesSubscription = this.serviceSocketService
          .onMessage(MessageSource.SHIFT)
          .subscribe((message: WebSocketEvent<ShiftEventType, ShiftEventData>) => {
              this.handleShiftEvent(message);
          });
        this.openSubscriptions.push(servicesSubscription);

        // subscribe to geometry cache updates
        const derivedLayerCacheEventSubscription = this.locationSocketService
            .onMessage(MessageSource.DERIVED_LAYER_CACHE)
            .subscribe((e: WebSocketEvent<string, LocationEventData>) => {
                const date = new Date(e.data.updated * 1000);
                this.handleDerivedLayerCacheUpdate(date);
                console.log(`Derived layer data updated: ${date}`);
            });
        this.openSubscriptions.push(derivedLayerCacheEventSubscription);
    }

    private handleShiftEvent(shiftEvent: WebSocketEvent<ShiftEventType, ShiftEventData>) {
        const driver = this.driverManager.getDriver(shiftEvent.data.driverId);
        const vehicle = this.vehiclesManager.getVehicle(shiftEvent.data.vehicleId);
        if (!!driver?.familyName && !!vehicle?.label) {
          let message: string;
          switch (shiftEvent.eventType) {
            case ShiftEventType.START:
              message = `Driver "${driver.givenName} ${driver.familyName}" has just started his shift with "${vehicle.label}".`;
              break;
            case ShiftEventType.UPDATE:
              switch (shiftEvent.data.shiftState) {
                case ShiftState.NORMAL:
                  message = `Vehicle "${vehicle.label}" is back in normal state`;
                  break;
                case ShiftState.STATIONARY:
                  message = `Vehicle "${vehicle.label}" is stationary`;
                  break;
                case ShiftState.PAUSED:
                  message = `Vehicle "${vehicle.label}" is paused`;
                  break;
              }
              break;
            case ShiftEventType.END:
              message = `Driver "${driver.givenName} ${driver.familyName}" has just ended his shift with "${vehicle.label}".`;
              break;
            default:
          }
          this.toast.long(message);
        }
    }

    private handleDerivedLayerCacheUpdate(date: Date) {
        this.derivedLayerCacheUpdateObservable.next(date);
    }
}
